import React, { useEffect, useState } from "react";
import {
  deleteAppointment,
  getCustomerAppointments,
  getServiceSlots,
  updateAppointment,
} from "../../../services/appointments";
import { useAuth } from "../../../context/AuthContext";
import "react-calendar/dist/Calendar.css";
import "./AppointmentsPage.css";
import ConfirmationModal from "../../../components/Modals/ConfirmationModal/ConfirmationModal";
import { Alert, CircularProgress, Snackbar } from "@mui/material";
import CustomCalendar from "../../../components/Calendar/CustomCalendar";

export default function AppointmentsPage() {
  const { token } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarContent, setSnackbarContent] = useState({ message: "", type: "" });
  const [appointmentsData, setAppointmentsData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);

  const [editingAppointmentToken, setEditingAppointmentToken] = useState(null);
  const [editingSlots, setEditingSlots] = useState([]);
  const [selectedEditDate, setSelectedEditDate] = useState(null);
  const [selectedEditSlot, setSelectedEditSlot] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    const fetchAppointments = async () => {
      try {
        const res = await getCustomerAppointments(token);
        setAppointmentsData(res.data);
      } catch (err) {
        console.error(err);
      }
      setIsLoading(false);
    };
    fetchAppointments();
  }, [token]);

  const formatDate = (dateInput) => {
    const date = new Date(dateInput);
    return (
      date.getFullYear() +
      "-" +
      String(date.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(date.getDate()).padStart(2, "0")
    );
  };

  const handleEditClick = async (appt) => {
    try {
      const res = await getServiceSlots(appt.service?.id, appt.branch?.id);
      setEditingSlots(res.data);
      setEditingAppointmentToken(appt.token); // 🔧 نمرر التوكين بدلاً من ID
    } catch (err) {
      console.error(err);
    }
  };

  const handleConfirmEdit = async () => {
    if (!selectedEditDate || !selectedEditSlot || !editingAppointmentToken) return;
    try {
      await updateAppointment(token, editingAppointmentToken, {
        appointmentDate: selectedEditDate,
        startTime: selectedEditSlot,
      });
      setEditingAppointmentToken(null);
      setSelectedEditDate(null);
      setSelectedEditSlot(null);
      const res = await getCustomerAppointments(token);
      setAppointmentsData(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAppointment(token, appointmentToDelete.token);
      setAppointmentsData((prev) =>
        prev.filter((item) => item.token !== appointmentToDelete.token)
      );
      setSnackbarOpen(true);
      setSnackbarContent({ message: "تم حذف الموعد بنجاح", type: "success" });
      setShowDeleteModal(false);
      setAppointmentToDelete(null);
    } catch (err) {
      console.error(err);
    }
  };

  const selectedDateAppointments = appointmentsData.filter(
    (item) => formatDate(item.date) === formatDate(selectedDate)
  );

  return (
    <div className="page">
      <div className="content-view">
        <h1 className="text-xl font-bold mb-4">إدارة المواعيد</h1>
        <div className="wrapper">
          {isLoading ? (
            <CircularProgress />
          ) : (
            <>
              {!editingAppointmentToken && (
                <div style={{ flex: 1 }}>
                  <CustomCalendar
                    value={selectedDate}
                    onChange={setSelectedDate}
                    tileClassName={({ date, view }) => {
                      if (view === "month") {
                        const dateStr = formatDate(date);
                        const hasAppointment = appointmentsData.some(
                          (item) => formatDate(item.date) === dateStr
                        );
                        return hasAppointment ? "has-appointment" : null;
                      }
                    }}
                    tileDisabled={({ date, view }) => {
                      if (view === "month") {
                        const dateStr = formatDate(date);
                        const hasAppointment = appointmentsData.some(
                          (item) => formatDate(item.date) === dateStr
                        );
                        return !hasAppointment;
                      }
                      return false;
                    }}
                  />
                </div>
              )}

              <div className="appointments-list">
                <h3>
                  المواعيد في{" "}
                  {selectedDate.toLocaleDateString("ar", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </h3>

                {selectedDateAppointments.length === 0 ? (
                  <p>لا توجد مواعيد في هذا اليوم.</p>
                ) : (
                  selectedDateAppointments.map((appt, index) => (
                    <div className="appointment-card editing-layout" key={index}>
                      {editingAppointmentToken === appt.token ? (
                        <div className="edit-content">
                          <div className="calendar-side">
                            <CustomCalendar
                              value={selectedEditDate ? new Date(selectedEditDate) : null}
                              onChange={(date) => {
                                const localISO = new Date(
                                  date.getTime() - date.getTimezoneOffset() * 60000
                                )
                                  .toISOString()
                                  .split("T")[0];
                                setSelectedEditDate(localISO);
                              }}
                              tileDisabled={({ date }) => {
                                const iso = new Date(
                                  date.getTime() - date.getTimezoneOffset() * 60000
                                )
                                  .toISOString()
                                  .split("T")[0];
                                return !editingSlots.some((s) => s.date === iso);
                              }}
                            />
                          </div>

                          <div className="slots-side">
                            {selectedEditDate && (
                              <div className="slots-grid">
                                {(
                                  editingSlots.find((s) => s.date === selectedEditDate)?.slots || []
                                ).map((slot, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => setSelectedEditSlot(slot.start)}
                                    className={`slot-button ${
                                      selectedEditSlot === slot.start ? "selected" : ""
                                    }`}
                                  >
                                    {slot.start} - {slot.end}
                                  </button>
                                ))}
                              </div>
                            )}
                            <div className="appointment-actions">
                              <button
                                className="btn small demo"
                                onClick={handleConfirmEdit}
                                disabled={!selectedEditSlot}
                              >
                                تأكيد التعديل
                              </button>
                              <button
                                className="btn small outline"
                                onClick={() => setEditingAppointmentToken(null)}
                              >
                                إلغاء
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="appointment-info">
                            <p>الفرع: {appt.branch?.name || "—"}</p>
                            <p>الخدمة: {appt.service?.name || "—"}</p>
                            <p>
                              الوقت: {appt.startTime} - {appt.endTime}
                            </p>
                          </div>
                          <div className="appointment-actions">
                            <button
                              className="btn small demo outline"
                              onClick={() => handleEditClick(appt)}
                            >
                              تعديل
                            </button>
                            <button
                              className="btn small demo-err outline"
                              onClick={() => {
                                setAppointmentToDelete(appt);
                                setShowDeleteModal(true);
                              }}
                            >
                              حذف
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarContent.type}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbarContent.message}
          </Alert>
        </Snackbar>
      </div>

      {showDeleteModal && (
        <ConfirmationModal
          message="هل أنت متأكد أنك تريد حذف هذا الموعد؟"
          handleConfirm={handleDelete}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
}
